// 後台「趨勢引擎判定台」（唯讀）
//
// 資料來源：regimeenginebar —— 每天 08:30 由 schedule/regimeEngineShadow.js 寫入的逐根判定。
// 這頁**不觸發計算、不接選策、不接下單**，只是把引擎說過的話攤開給人看。
//
// ⚠️ 判定【尚未驗證】。前向檢定的主分析最早在台北 2028-10-08，在那之前沒有任何預測力證據。
//    頁首的警語不是裝飾：拿掉它，這頁看起來就像一個可以照著做的多空訊號。
//
// ⚠️ brokerTime 是券商時間 GMT+2/+3，後端已格式化成字串直出。
//    **絕對不要**把它丟進 utils/datetime.js 的 fmtDateTime——那支是為「DB 存 UTC」設計的，
//    餵券商時間會再偏移一次觀看者時區。
//
// ⚠️ 證據力**不看 isBackfill**。isBackfill 只是營運訊號（這批算得晚）。
//    證據力看的是：該列的 engineSha256／parameterHash 是否等於前向檢定凍結版，
//    以及 K 棒時間是否晚於凍結時點（後端每列給 evidenceClass）。
//    這頁曾經寫「補算列不具前向證據力」，那是已被推翻的舊判準，不要寫回來。
//
// ⚠️ 池佔比只統計【現行引擎版本】（最新一次成功批次的 sha＋參數雜湊）。影子表不覆蓋舊列，
//    換過引擎後不同版本的列會並存；混在一起算出來的佔比不屬於任何一版引擎。
//
// 代碼→中文、每列怎麼呈現的純邏輯在 ./labels.js（node 可直接測）。

import { useState, useEffect, useRef, useCallback } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import {
  TextField, MenuItem, Chip, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "AuthContext";
import { COLORS } from "theme/tokens";
import {
  MAIN_ANALYSIS_NOT_BEFORE, reasonZh, regimeView, evidenceView, fmtPct, shortSha, symbolOptionLabel,
} from "./labels";

const POOL = {
  directional: { label: "方向池", color: "#15795F" },
  range: { label: "盤整池", color: "#8F6A14" },
  none: { label: "暫不選策", color: COLORS.grayDark },
};

const fmtInt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
/** 券商時間字串只顯示到分鐘（後端的凍結時點帶秒）。純字串截斷，不做任何時區換算。 */
const toMinute = (s) => (s ? String(s).slice(0, 16) : "—");

function StatTile({ label, value, note, color }) {
  return (
    <Card sx={{ p: 2.5, height: "100%" }}>
      <MDTypography variant="button" color="text" fontWeight="medium">{label}</MDTypography>
      <MDTypography variant="h3" fontWeight="bold" sx={{ color: color || COLORS.black, lineHeight: 1.2 }}>
        {value ?? "—"}
      </MDTypography>
      {note ? (
        <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>{note}</MDTypography>
      ) : null}
    </Card>
  );
}

/** 頁首警語：判定尚未驗證。不管 API 有沒有讀到都要顯示。 */
function UnvalidatedBanner({ notBefore }) {
  return (
    <Card sx={{ p: 2.5, mb: 2, border: `2px solid ${COLORS.error}`, bgcolor: COLORS.lighter }}>
      <MDTypography variant="h6" fontWeight="bold" sx={{ color: COLORS.red }}>
        尚未驗證｜不得作為交易依據
      </MDTypography>
      <MDTypography variant="button" sx={{ display: "block", mt: 0.5, color: COLORS.black }}>
        這是<b>影子模式</b>：引擎每天把判定記下來，但<b>判定尚未經前向檢定驗證</b>
        （主分析最早在台北時間 {notBefore}，而且只做一次）。目前沒有任何證據顯示這些判定有預測力。
      </MDTypography>
      <MDTypography variant="button" sx={{ display: "block", mt: 0.5, color: COLORS.black }}>
        本頁的「多頭／空頭」只是對近期走勢的描述，不是預測。判定<b>不接選策、不接下單</b>；
        任何人都不得拿本頁的狀態或路由當作交易依據。
      </MDTypography>
    </Card>
  );
}

/** 狀態帶：每根一條細直線，顏色＝路由。比 K 線更能一眼看出「引擎大部分時間在說什麼」。 */
function RouteRibbon({ bars }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv || !bars.length) return;
    const dpr = window.devicePixelRatio || 1;
    const w = cv.clientWidth;
    const h = 56;
    cv.width = Math.floor(w * dpr);
    cv.height = Math.floor(h * dpr);
    const ctx = cv.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const bw = w / bars.length;
    // 舊版引擎算的列畫淡：它們不計入頁面上的佔比。
    // （刻意不再用 isBackfill 調淡——算得晚不代表比較不可信，那是已推翻的判準。）
    bars.forEach((b, i) => {
      ctx.fillStyle = (POOL[b.pool] || POOL.none).color;
      ctx.globalAlpha = b.isCurrentEngine === false ? 0.4 : 1;
      ctx.fillRect(i * bw, 0, Math.max(bw, 0.6), h - 14);
    });
    ctx.globalAlpha = 1;
    bars.forEach((b, i) => {
      if (b.isCurrentEngine !== false) return;
      ctx.fillStyle = COLORS.grayMedian;
      ctx.fillRect(i * bw, h - 10, Math.max(bw, 0.6), 3);
    });
  }, [bars]);
  return <canvas ref={ref} style={{ width: "100%", height: 56, display: "block" }} />;
}

/** 「引擎判定」欄：沒被路由採用的狀態要明顯降階，不能長得像一個可用的多空方向 */
function RegimeCell({ bar }) {
  const v = regimeView(bar);
  if (v.adopted) {
    return (
      <span style={{ color: (POOL[bar.pool] || POOL.none).color, fontWeight: 700 }}>{v.label}</span>
    );
  }
  return (
    <span style={{ color: COLORS.grayDark }}>
      <span style={{ textDecoration: v.isDirection ? "line-through" : "none" }}>{v.label}</span>
      <span
        title={v.isDirection
          ? "引擎的原始判定是這個方向，但這一根沒有進方向池——不是可採用的多空方向"
          : "這一根沒有進任何一池"}
        style={{
          marginLeft: 6, padding: "0 6px", fontSize: 11, borderRadius: 4,
          border: `1px solid ${COLORS.grayMedian}`, color: COLORS.grayDark, whiteSpace: "nowrap",
        }}
      >
        不採用
      </span>
      {v.inconsistent ? (
        <span style={{ marginLeft: 6, color: COLORS.error, fontSize: 11 }}>路由與狀態對不上</span>
      ) : null}
    </span>
  );
}

/** 「為什麼不選」欄：代碼一律翻成中文；對不到的原樣顯示並標「未翻譯」，不會是空白 */
function ReasonCell({ bar }) {
  if (bar.pool !== "none") return "—";
  const seen = new Set();
  const items = [];
  [bar.reasonCode, ...(bar.ineligibleReasons || [])].forEach((c) => {
    if (c === null || c === undefined || c === "" || seen.has(c)) return;
    seen.add(c);
    items.push(reasonZh(c));
  });
  if (!items.length) return "（引擎未給理由）";
  return items.map((it, i) => (
    <span key={it.code}>
      {i ? "／" : ""}
      <span title={it.code} style={it.translated ? undefined : { color: COLORS.error, fontFamily: "monospace" }}>
        {it.text}
      </span>
    </span>
  ));
}

export default function RegimeConsole() {
  const { token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [symbol, setSymbol] = useState("");
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(false);
  // 預設 false＝加這個開關之前的行為（全部商品都列出來）；停更的只是排到後面並加標示
  const [hideStale, setHideStale] = useState(false);

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/regime-console/overview", auth());
      const d = await res.json();
      setOverview(d);
      // 後端已把停更商品排到最後，所以第一個就是「有在更新的第一個商品」
      if (d.ready && d.symbols?.length && !symbol) setSymbol(d.symbols[0].symbol);
    } catch (e) {
      console.error("regime overview error:", e);
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [auth, symbol]);

  const loadBars = useCallback(async (sym) => {
    if (!sym) return;
    try {
      const res = await fetch(`/api/admin/regime-console/bars?symbol=${encodeURIComponent(sym)}&limit=720`, auth());
      const d = await res.json();
      setBars(d.ready ? d.bars || [] : []);
    } catch (e) {
      console.error("regime bars error:", e);
      setBars([]);
    }
  }, [auth]);

  useEffect(() => { loadOverview(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadBars(symbol); }, [symbol, loadBars]);

  const allSymbols = overview?.symbols || [];
  const shownSymbols = hideStale ? allSymbols.filter((s) => !s.stale) : allSymbols;
  const sym = allSymbols.find((s) => s.symbol === symbol);
  const base = overview?.baseline;
  const run = overview?.lastRun;
  const cur = overview?.currentEngine;
  const freeze = overview?.freeze;
  const fresh = overview?.freshness;
  const staleList = fresh?.staleSymbols || [];
  const versions = overview?.versions || [];
  const recent = bars.slice(-40).reverse();
  const notBefore = freeze?.analysisNotBeforeTaipei || MAIN_ANALYSIS_NOT_BEFORE;
  // 前端已更新、server 還沒重啟到新版時，回應裡不會有 currentEngine 這個欄位（新版沒有成功批次時是 null，不是 undefined）。
  // 這時拿到的佔比是舊算法（各版本混算），必須講清楚，不能讓它看起來像「現行版本的佔比」。
  const backendOutdated = Boolean(overview?.ready) && overview.currentEngine === undefined;

  const onToggleHideStale = (e) => {
    const next = e.target.checked;
    setHideStale(next);
    // 正在看的商品被篩掉 → 換到第一個沒停更的，否則下拉會指向一個不存在的選項
    if (next && sym?.stale) {
      const first = allSymbols.find((s) => !s.stale);
      if (first) setSymbol(first.symbol);
    }
  };

  const baseNote = (key) => {
    if (!sym) return "";
    if (backendOutdated) return "版本不明（後端尚未更新）";
    if (!sym.currentBars) return "現行版本沒有樣本";
    const b = base ? `｜長窗基準 ${base[key]}%` : "";
    return `現行版本 ${fmtInt(sym.currentBars)} 根${b}`;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={2}>
          <MDTypography variant="h4" fontWeight="bold">趨勢引擎判定台</MDTypography>
          <MDTypography variant="button" color="text">
            已收盤 H1 K 棒 → 資料品質 → 市場狀態 → 三池路由
          </MDTypography>
          <MDBox mt={1} display="flex" gap={1} flexWrap="wrap">
            <Chip size="small" label="影子模式 · 未接選策 / 未接下單" sx={{ bgcolor: COLORS.lighter, color: COLORS.main, fontWeight: 600 }} />
            <Chip size="small" label="判定尚未驗證" sx={{ bgcolor: COLORS.error, color: COLORS.white, fontWeight: 600 }} />
          </MDBox>
        </MDBox>

        <UnvalidatedBanner notBefore={notBefore} />

        {!overview ? (
          <Card sx={{ p: 3 }}>
            <MDTypography variant="button" color="text">{loading ? "讀取中…" : "讀取失敗"}</MDTypography>
          </Card>
        ) : !overview.ready ? (
          <Card sx={{ p: 3 }}>
            <MDTypography variant="h6" sx={{ color: COLORS.warning }}>尚未啟用</MDTypography>
            <MDTypography variant="button" color="text" sx={{ display: "block", mt: 1 }}>
              {overview.message}
            </MDTypography>
          </Card>
        ) : (
          <>
            {/* 批次狀態：這頁的資料有沒有在更新，比任何圖都重要 */}
            <Card sx={{ p: 2.5, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <MDTypography variant="button" color="text" fontWeight="medium">最近一次批次</MDTypography>
                  <MDTypography variant="h6">
                    {run ? `${run.status}｜${new Date(run.startedAt).toLocaleString()}` : "尚未跑過"}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDTypography variant="button" color="text" fontWeight="medium">來源資料最新</MDTypography>
                  <MDTypography variant="h6" sx={{ color: run?.dataStaleDays > 3 ? COLORS.error : COLORS.black }}>
                    {run?.dataMaxTime || "—"}
                    {run?.dataStaleDays !== null && run?.dataStaleDays !== undefined
                      ? `（落後 ${run.dataStaleDays} 天）` : ""}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDTypography variant="button" color="text" fontWeight="medium">重算分歧</MDTypography>
                  <MDTypography variant="h6" sx={{ color: run?.divergences ? COLORS.error : COLORS.black }}>
                    {run?.divergences ?? 0}
                    {run?.divergences ? "（既存列未被覆蓋）" : ""}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDButton variant="outlined" color="info" size="small" onClick={loadOverview} disabled={loading}>
                    重新整理
                  </MDButton>
                </Grid>
              </Grid>
              {run?.dataStaleDays > 3 ? (
                <MDTypography variant="caption" sx={{ color: COLORS.error, display: "block", mt: 1 }}>
                  ⚠️ 來源資料落後超過 3 天，每日 MT5 歷史抓取（08:00）可能沒在跑。引擎只是在重算舊資料。
                </MDTypography>
              ) : null}
              {backendOutdated ? (
                <MDTypography variant="caption" sx={{ color: COLORS.error, display: "block", mt: 1 }}>
                  ⚠️ 後端回應沒有引擎版本與凍結資訊（server 還沒重啟到新版？）。下方佔比可能把不同引擎版本混在一起算，<b>請勿引用</b>。
                </MDTypography>
              ) : (
                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1 }}>
                  {cur
                    ? `現行引擎（最新一次成功批次）：${cur.engineVersion || "未知版本"}｜sha ${shortSha(cur.engineSha256)}｜參數 ${cur.parameterHash}`
                    : "還沒有任何成功的批次，無法界定現行引擎版本——下方佔比一律不計算。"}
                  {cur ? (cur.matchesFrozen ? "｜＝前向檢定凍結版" : "") : ""}
                </MDTypography>
              )}
              {cur && !cur.matchesFrozen ? (
                <MDTypography variant="caption" sx={{ color: COLORS.error, display: "block" }}>
                  ⚠️ 現行引擎不是前向檢定的凍結版：這一版寫入的列，前向檢定不會稽核。
                </MDTypography>
              ) : null}
              {staleList.length ? (
                <MDTypography variant="caption" sx={{ color: COLORS.error, display: "block", mt: 1 }}>
                  ⚠️ {staleList.length} 個商品的價格來源已停更（落後全體最新時間 {fresh?.globalLatest} 超過 {fresh?.staleDays} 天）：
                  {staleList.map((s) => `${s.symbol}（落後 ${s.lagDays} 天，停在 ${s.latest}）`).join("、")}。
                  它們的判定停在舊日期，<b>不代表現況</b>。
                </MDTypography>
              ) : null}
            </Card>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={3}>
                <StatTile label="暫不選策" color={POOL.none.color}
                  value={sym ? fmtPct(sym.nonePct) : "—"} note={baseNote("nonePct")} />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatTile label="方向池可選" color={POOL.directional.color}
                  value={sym ? fmtPct(sym.directionalPct) : "—"} note={baseNote("directionalPct")} />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatTile label="盤整池可選" color={POOL.range.color}
                  value={sym ? fmtPct(sym.rangePct) : "—"} note={baseNote("rangePct")} />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatTile label="樣本外列數（凍結後）"
                  value={sym ? fmtInt(sym.evidence?.afterFreezeBars) : "—"}
                  note={sym
                    ? `其中在正式檢定窗內 ${fmtInt(sym.evidence?.inTestWindowBars)} 根｜凍結版引擎共 ${fmtInt(sym.evidence?.frozenEngineBars)} 根`
                    : ""} />
              </Grid>
            </Grid>

            {/* 佔比的版本與樣本區間：沒有這兩樣，上面三個百分比沒辦法被正確引用 */}
            <Card sx={{ p: 2.5, mb: 2 }}>
              {backendOutdated ? (
                <MDTypography variant="caption" sx={{ color: COLORS.error, display: "block" }}>
                  <b>上面三個佔比的引擎版本不明。</b> 後端還沒更新，回來的數字可能混了不同版本，請勿引用。
                </MDTypography>
              ) : (
                <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                  <b>上面三個佔比只統計現行引擎版本。</b>
                  {cur ? ` 版本 ${cur.engineVersion || "未知版本"}（sha ${shortSha(cur.engineSha256)}、參數 ${cur.parameterHash}）。` : " 目前無法界定現行版本。"}
                  {sym && sym.currentBars
                    ? ` 本商品樣本：${sym.sampleFrom} → ${sym.sampleTo}（券商時間），${fmtInt(sym.currentBars)} 根。`
                    : sym ? " 本商品在現行版本下沒有任何判定，所以不顯示佔比。" : ""}
                  {sym && sym.otherVersionBars ? ` 另有 ${fmtInt(sym.otherVersionBars)} 根是其他版本引擎算的，不計入。` : ""}
                  {" "}樣本只有幾天到幾週，不代表常態；要引用請用頁尾的長窗基準。
                </MDTypography>
              )}
              <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1 }}>
                <b>證據力怎麼看：不看「即時／事後補算」。</b>看兩件事——
                ① 這一列的 engineSha256／parameterHash 是否等於前向檢定的凍結版
                {freeze ? `（${freeze.engineVersion}、sha ${shortSha(freeze.engineSha256)}、參數 ${freeze.parameterHash}）` : ""}；
                ② K 棒時間是否晚於凍結時點{freeze ? `（券商時間 ${toMinute(freeze.cutoffBrokerTime)}）` : ""}。
                兩者都成立才是樣本外。
                {freeze ? ` 其中只有落在正式檢定窗（券商時間 ${toMinute(freeze.windowStartBrokerTime)} ～ ${toMinute(freeze.windowEndBrokerTime)}）的列會計入主分析。` : ""}
              </MDTypography>
              <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1 }}>
                <b>「事後補算」（isBackfill）只是營運訊號</b>：表示這一列算得晚、批次沒跟上，該去查資料來源；
                它不影響這一列是不是樣本外。
                {sym ? ` 本商品：即時 ${fmtInt(sym.liveBars)} 根／晚算 ${fmtInt(sym.backfillBars)} 根。` : ""}
              </MDTypography>
            </Card>

            <Card sx={{ p: 2.5, mb: 2 }}>
              <MDBox display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                <TextField select size="small" label="商品" value={symbol}
                  onChange={(e) => setSymbol(e.target.value)} sx={{ minWidth: 260 }}>
                  {shownSymbols.map((s) => (
                    <MenuItem key={s.symbol} value={s.symbol} sx={s.stale ? { color: COLORS.grayDark } : undefined}>
                      {symbolOptionLabel(s)}
                    </MenuItem>
                  ))}
                </TextField>
                {staleList.length ? (
                  <FormControlLabel
                    control={<Switch size="small" checked={hideStale} onChange={onToggleHideStale} />}
                    label={<MDTypography variant="caption">隱藏資料落後的商品（{staleList.length}）</MDTypography>}
                  />
                ) : null}
                {sym ? (
                  <MDTypography variant="button" color="text">
                    {sym.from} → {sym.to}　券商時間 GMT+2/+3
                  </MDTypography>
                ) : null}
                <MDBox display="flex" gap={1.5} ml="auto">
                  {Object.entries(POOL).map(([k, v]) => (
                    <MDBox key={k} display="flex" alignItems="center" gap={0.5}>
                      <MDBox sx={{ width: 12, height: 12, bgcolor: v.color, borderRadius: "2px" }} />
                      <MDTypography variant="caption">{v.label}</MDTypography>
                    </MDBox>
                  ))}
                </MDBox>
              </MDBox>
              {sym?.stale ? (
                <MDBox mb={1.5} p={1.5} sx={{ border: `1px solid ${COLORS.error}`, borderRadius: "8px", bgcolor: COLORS.lighter }}>
                  <MDTypography variant="button" fontWeight="bold" sx={{ color: COLORS.red, display: "block" }}>
                    資料落後 {sym.lagDays} 天
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ color: COLORS.black, display: "block" }}>
                    {sym.symbol} 的價格資料停在 {sym.priceTo || sym.to}（券商時間），全體最新是 {fresh?.globalLatest}。
                    下面看到的是當時的判定，不是現在的。
                  </MDTypography>
                </MDBox>
              ) : null}
              {sym && !sym.stale && sym.shadowBehindPrice ? (
                <MDTypography variant="caption" sx={{ color: COLORS.error, display: "block", mb: 1 }}>
                  ⚠️ 價格表已經到 {sym.priceTo}，但影子判定只到 {sym.to}：每日批次還沒跟上。
                </MDTypography>
              ) : null}
              {bars.length ? <RouteRibbon bars={bars} /> : (
                <MDTypography variant="button" color="text">這個商品還沒有任何判定紀錄。</MDTypography>
              )}
              <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1 }}>
                每一條細線＝一根 H1，顏色＝路由。畫得比較淡、下面帶灰色底線的區段是舊版引擎算的，不計入上面的佔比。
              </MDTypography>
            </Card>

            <Card sx={{ p: 2.5 }}>
              <MDTypography variant="h6" mb={1}>最近 40 根</MDTypography>
              <TableContainer sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {["券商時間", "引擎判定", "路由", "D", "T", "為什麼不選", "證據力"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recent.map((b) => {
                      const ev = evidenceView(b.evidenceClass);
                      return (
                        <TableRow key={b.barId} sx={{ opacity: b.isCurrentEngine === false ? 0.6 : 1 }}>
                          <TableCell sx={{ whiteSpace: "nowrap", fontFamily: "monospace" }}>
                            {b.brokerTime}
                            {b.isBackfill ? (
                              <span title="營運訊號：這一列算得晚（批次沒跟上）。不影響它是不是樣本外。"
                                style={{ marginLeft: 6, color: COLORS.grayDark, fontSize: 11 }}>
                                晚算
                              </span>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <RegimeCell bar={b} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", color: (POOL[b.pool] || POOL.none).color, fontWeight: 600 }}>
                            {(POOL[b.pool] || POOL.none).label}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{b.D === null ? "—" : b.D.toFixed(3)}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{b.T === null ? "—" : b.T.toFixed(3)}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            <ReasonCell bar={b} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>
                            <span title={ev.long}
                              style={{ color: ev.outOfSample ? COLORS.black : COLORS.grayDark, fontWeight: ev.outOfSample ? 700 : 400 }}>
                              {ev.short}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1 }}>
                「引擎判定」欄裡劃掉並標「不採用」的多頭／空頭，是引擎的原始判定但這一根沒有進方向池——<b>不是可採用的方向</b>。
                「證據力」欄的樣本外／檢定窗內，也只代表這一列可以拿來驗證，<b>不代表判定已被驗證</b>。
              </MDTypography>
            </Card>

            <MDBox mt={2}>
              <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                <b>這頁不是產品，是判定台。</b> 引擎不接觸下單路徑；此處只把它對真實 H1 的判定逐根攤開供人工檢視。
              </MDTypography>
              <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                <b>三池候選清單尚未接上。</b> 路由只說「可以考慮哪一池」，池裡放什麼策略是另一件事。「暫不選策」不等於「選盤整池」。
              </MDTypography>
              <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                <b>門檻未經校準。</b> DIRECTION ±0.35、TREND 0.40 皆為原規格預設值，尚未用真實資料校準，且不得為提高覆蓋率而調低。
              </MDTypography>
              {base ? (
                <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                  <b>長窗基準</b>（{base.measuredAt}、{base.engineVersion}、{base.symbols} 商品、{base.bars.toLocaleString()} 根）：
                  方向池 {base.directionalPct}%、盤整池 {base.rangePct}%、暫不選策 {base.nonePct}%。引用請用這組，不要用本頁切片。
                </MDTypography>
              ) : null}
              {versions.length ? (
                <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                  <b>影子表裡的引擎版本</b>：
                  {versions.map((v) => (
                    `${v.isCurrent ? "現行" : "其他"} ${v.engineVersion || "未知版本"}（sha ${shortSha(v.engineSha256)}、參數 ${v.parameterHash}）`
                    + ` ${fmtInt(v.bars)} 列，${v.from} → ${v.to}${v.isCurrent ? "" : "，不計入佔比"}`
                  )).join("；")}。
                </MDTypography>
              ) : null}
              {run ? (
                <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                  最近一次批次的引擎 {run.engineVersion}（{shortSha(run.engineSha256, 16)}）
                </MDTypography>
              ) : null}
            </MDBox>
          </>
        )}
      </MDBox>
    </DashboardLayout>
  );
}
